import PropTypes from "prop-types";
import React from "react";

import {
	BulletCard,
	Card,
	MiniTab,
	ContentCard,
	DropdownCard,
	HeaderCard,
	LoaderCard,
	SubTitleCard,
	TitleCard
} from "@weezevent/nacre";
import Style from "./frameCard.module.scss";

const FrameCard = ({
	active,
	loading,
	children,
	items,
	panes,
	headerOnClick,
	subtitle,
	bulletCard,
	dropdown,
	onExternalTabChange,
	separator,
	className,
	id,
	activeTab = 0,
	...rest
}) => {
	const has_children = React.Children.count(children) > 0;
	const has_content = has_children || active || loading;
	separator = has_content ? separator : false;

	const [internalActiveIndex, setInternalActiveIndex] = React.useState(
		panes?.length ? 0 : -1
	);

	const activeIndex = React.useMemo(() => activeTab ?? internalActiveIndex, [
		activeTab,
		internalActiveIndex
	]);

	//New logic for ContentCard
	const currentContent = React.useMemo(() => {
		if (panes && panes.length > 0) {
			return panes[activeIndex]?.content;
		}
		return children;
	}, [panes, activeIndex, children]);

	const cardProps = React.useMemo(() => {
		if (!id) {
			return {};
		}
		const props = {};
		props.id = id;
		return props;
	}, [id]);

	return (
		<Card className={className} {...cardProps}>
			<HeaderCard
				onClick={headerOnClick}
				separator={separator}
				style={{ position: "relative" }}
			>
				{bulletCard ? <BulletCard {...bulletCard} /> : null}
				<TitleCard {...rest} />
				{subtitle ? <SubTitleCard title={subtitle} /> : null}
				{dropdown || items ? (
					<DropdownCard items={items} {...dropdown} />
				) : null}
				{panes && (
					<div className={Style.headerCardMiniTab}>
						<MiniTab
							panes={panes}
							activeTab={panes?.[activeIndex]?.value}
							onTabSelected={(_, index) => {
								setInternalActiveIndex(index);
								if (onExternalTabChange) {
									onExternalTabChange(index);
								}
							}}
						/>
					</div>
				)}
			</HeaderCard>
			{has_content ? (
				<ContentCard>
					<LoaderCard active={active || loading} />
					{currentContent}
				</ContentCard>
			) : null}
		</Card>
	);
};

FrameCard.propTypes = {
	/**
	 * Activated the spinner
	 */
	active: PropTypes.bool,
	/**
	 * Activates the spinner with a normal name
	 */
	loading: PropTypes.bool,
	/**
	 * Add the bulletCard props
	 */
	bulletCard: PropTypes.shape({
		className: PropTypes.string,
		color: PropTypes.string,
		custom: PropTypes.string,
		onClick: PropTypes.func
	}),
	/**
	 * Children render inside the contentCard and below the loaderCard
	 */
	children: PropTypes.node,
	/**
	 * Props to manage the dropdown
	 */
	dropdown: PropTypes.shape({
		closeOnDocumentClick: PropTypes.bool,
		disabled: PropTypes.bool,
		onChange: PropTypes.func,
		onClose: PropTypes.func,
		onLeaving: PropTypes.func,
		onOpen: PropTypes.func,
		isLoading: PropTypes.bool,
		items: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
		size: PropTypes.oneOf(["mini", "medium", "maxi"])
	}),
	/**
	 * Add dropdownItem instances
	 */
	items: PropTypes.oneOfType([PropTypes.array, PropTypes.node]),
	/**
	 * Add MiniTab instances
	 */
	panes: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
			icon: PropTypes.string,
			disabled: PropTypes.bool,
			content: PropTypes.node.isRequired
		})
	),
	/**
	 * External callback on tab change
	 */
	onExternalTabChange: PropTypes.func,
	/**
	 * Click to add on the header. It won't be fired when you will click
	 * on the dropdown if one exists
	 */
	headerOnClick: PropTypes.func,
	/**
	 * Add a subtitle (title)
	 */
	subtitle: PropTypes.node,
	/**
	 * Add a separator to the header
	 */
	separator: PropTypes.bool,
	/**
	 * Add classname
	 */
	className: PropTypes.string,
	/**
	 * Add an id to the card
	 */
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	/**
	 * Add activeTab
	 */
	activeTab: PropTypes.number
};

FrameCard.defaultProps = {
	active: false,
	loading: false,
	bulletCard: null,
	children: null,
	dropdown: null,
	items: null,
	panes: null,
	onExternalTabChange: null,
	headerOnClick: null,
	subtitle: null,
	separator: true,
	className: "",
	id: null,
	activeTab: null
};

export default FrameCard;
